import { useState } from 'react';

// Quadro de ideias do agente de Ideação: cada ideia traz o formato (do banco de formatos), as
// duas aberturas (A e B, para Trial Reels), os fatos concretos, o motivo de envio e a evidência.
// "Produzir esta ideia" manda o id para a Produção; o backend anexa o texto completo do registro.

export interface IdeaItem {
  id: string;
  title: string;
  format: string;
  formatType?: string;
  hook: string;
  onScreen?: string;
  openingB?: { hook: string; onScreen?: string } | null;
  angle?: string;
  evidence?: string;
  facts?: string[];
  shareReason?: string;
  cautions?: string;
  references?: string[];
  brief?: string;
}

const FORMAT_LABEL: Record<string, string> = { reel: 'Reel', carrossel: 'Carrossel', post: 'Post', story: 'Story' };

// Mesmo banco de formatos do backend (src/services/creativeFormats.ts).
const FORMAT_TYPE_LABEL: Record<string, string> = {
  'pov-humor': 'POV / meme',
  'erro-que-custa': 'Erro que custa',
  ranking: 'Ranking',
  'asmr-loop': 'ASMR em loop',
  'anti-anuncio': 'Anti-anúncio',
  'historia-3-atos': 'História 3 atos',
  quiz: 'Quiz',
  'tutorial-relampago': 'Tutorial-relâmpago',
  recorrente: 'Recorrente',
  'bastidores-lista': 'Ninguém te conta',
  checklist: 'Checklist',
  'mito-verdade': 'Mito × verdade',
  'antes-depois': 'Antes / depois',
  'passo-a-passo': 'Passo a passo',
  comparacao: 'Comparação',
  bastidor: 'Bastidor',
  'resultado-em-uso': 'Resultado em uso',
  'a-conta': 'A conta',
  'ideias-de-uso': 'Ideias de uso',
  'prova-de-detalhe': 'Prova de detalhe',
  'oferta-direta': 'Oferta direta',
  outro: 'Outro formato',
};

export function IdeasCard({ p, disabled, onProduce }: {
  p: { title?: string; summary?: string; ideas?: IdeaItem[] };
  disabled: boolean;
  onProduce: (idea: IdeaItem) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const ideas = Array.isArray(p.ideas) ? p.ideas : [];
  return (
    <div className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm">
      <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-white px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500">Quadro de ideias · Ideação</p>
        <p className="text-sm font-bold text-slate-900">{p.title || 'Ideias de conteúdo'}</p>
        {p.summary && <p className="mt-1 text-xs leading-relaxed text-slate-600">{p.summary}</p>}
      </div>
      <ol className="divide-y divide-slate-100">
        {ideas.map((idea, i) => (
          <li key={idea.id || i} className="px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[11px] font-black text-violet-700">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-slate-900">{idea.title}</p>
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">{FORMAT_LABEL[idea.format] || idea.format}</span>
                  {idea.formatType && idea.formatType !== 'outro' && (
                    <span className="rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-600">{FORMAT_TYPE_LABEL[idea.formatType] || idea.formatType}</span>
                  )}
                </div>
                <p className="mt-1 text-sm text-slate-800"><span className="mr-1 text-[10px] font-bold text-slate-400">A</span>“{idea.hook}”</p>
                {idea.onScreen && <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{idea.format === 'carrossel' || idea.format === 'post' ? 'Capa' : 'Na tela'}: {idea.onScreen}</p>}
                {idea.openingB?.hook && (
                  <p className="mt-1 text-sm text-slate-600"><span className="mr-1 text-[10px] font-bold text-slate-400">B</span>“{idea.openingB.hook}”{idea.openingB.onScreen ? <span className="ml-1 text-[11px] uppercase text-slate-400">· {idea.openingB.onScreen}</span> : null}</p>
                )}
                {idea.shareReason && <p className="mt-1 text-[11px] text-slate-500"><b>Por que mandam:</b> {idea.shareReason}</p>}
                {idea.evidence && (
                  <p className="mt-1.5 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] leading-relaxed text-emerald-800"><b>Evidência:</b> {idea.evidence}</p>
                )}
                {open === idea.id && (
                  <div className="mt-2 space-y-1 text-xs leading-relaxed text-slate-600">
                    {idea.angle && <p><b>Ângulo:</b> {idea.angle}</p>}
                    {!!idea.facts?.length && (
                      <div><b>Fatos:</b><ul className="ml-4 list-disc">{idea.facts.map((f, k) => <li key={k}>{f}</li>)}</ul></div>
                    )}
                    {idea.brief && <p className="whitespace-pre-wrap"><b>Brief:</b> {idea.brief}</p>}
                    {idea.cautions && <p className="text-amber-700"><b>Cuidados:</b> {idea.cautions}</p>}
                    {!!idea.references?.length && (
                      <p className="flex flex-wrap gap-1.5"><b>Referências:</b>{idea.references.map(r => /^https?:/.test(r)
                        ? <a key={r} href={r} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{r.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}</a>
                        : <span key={r}>{r}</span>)}</p>
                    )}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={disabled || !!busy}
                    onClick={async () => { setBusy(idea.id); try { await onProduce(idea); } finally { setBusy(null); } }}
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-40"
                  >
                    {busy === idea.id ? 'Enviando…' : 'Produzir esta ideia'}
                  </button>
                  <button type="button" onClick={() => setOpen(open === idea.id ? null : idea.id)} className="text-xs font-semibold text-slate-500 hover:text-slate-800">
                    {open === idea.id ? 'Menos' : 'Ver brief'}
                  </button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
